<script setup lang="ts">
import {
    B4Expr,
    type B4ExprProp,
} from '@precon-tools-suite-by-shared/gino-multi-valued-logic-purescript';
import { PredicateValue } from '@precon-tools-suite-by-shared/precon-graphql-types';
import NodeReprInput, {
    type PredicateValueInputMethod,
    type PredicateValueDisplayFormat,
    type ValueSlotProps,
    type TermSlotProps,
} from './NodeReprInput.vue';
import { NodeRepr, reprFromExpr, exprFromRepr } from '../helpers/internal-repr';
import { type VNode, computed, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        modelValue?: B4ExprProp;
        valueLabelFormat?: PredicateValueDisplayFormat;
        valueInputMethod?: PredicateValueInputMethod;
        inputId?: string;
        inputName?: string;
        allowEquiv?: boolean;
        allowNonRootValue?: boolean;
        allowUndecidableValue?: boolean;
        symbolLabels?: boolean;
        symbolValueLabels?: boolean;
        readonly?: boolean;
        testTermBindings?: Record<string, PredicateValue> | null;
        testTautologies?: boolean;
        testContradictions?: boolean;
        renderValue?: ((props: ValueSlotProps) => VNode[]) | null;
        renderTerm?: ((props: TermSlotProps) => VNode[]) | null;
    }>(),
    {
        valueLabelFormat: 'answer',
        valueInputMethod: 'buttons',
        modelValue: () => B4Expr.INDETERMINATE,
        inputId: (props) => props?.inputName ?? 'b4-expr-input',
        inputName: (props) => props?.inputId ?? 'b4-expr-input',
        renderValue: null,
        renderTerm: null,
        testTermBindings: null,
        testTautologies: true,
        testContradictions: true,
        readonly: false,
    }
);

defineSlots<{
    value: (props: ValueSlotProps) => any;
    term: (props: TermSlotProps) => any;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value: B4Expr): void;
    (e: 'update:testTermBindings', value: Record<string, PredicateValue>): void;
}>();

let _lastEmittedExpr: B4Expr | undefined = undefined;

const expr = computed({
    get: () => B4Expr.from(props.modelValue),
    set: (newValue: B4Expr) => {
        if (!props.allowNonRootValue) {
            newValue = newValue.elimClassicalValues();
        }
        _lastEmittedExpr = newValue;
        emit('update:modelValue', newValue);
    },
});

const _repr = ref(reprFromExpr(expr.value));

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue !== _lastEmittedExpr) {
            _repr.value = reprFromExpr(expr.value);
        }
    }
);

const repr = computed<NodeRepr>({
    get: () => _repr.value,
    set: (newValue: NodeRepr) => {
        _repr.value = newValue;
        expr.value = exprFromRepr(_repr.value) ?? B4Expr.INDETERMINATE;
    },
});

const internalTestTermBindings = ref(props.testTermBindings ?? {});

const termBindings = computed({
    get: () => props.testTermBindings ?? internalTestTermBindings.value,
    set: (newValue) => {
        internalTestTermBindings.value = newValue ?? {};
        emit('update:testTermBindings', newValue);
    },
});
</script>

<template>
    <NodeReprInput
        v-model="repr"
        v-model:test-term-bindings="termBindings"
        :value-input-method="valueInputMethod"
        :value-label-format="valueLabelFormat"
        class="b4expr-input"
        :readonly="readonly"
        :allow-equiv="allowEquiv"
        :allow-non-root-value="allowNonRootValue"
        :allow-undecidable-value="allowUndecidableValue"
        :symbol-labels="symbolLabels"
        :symbol-value-labels="symbolValueLabels"
        :test-tautologies="testTautologies"
        :test-contradictions="testContradictions"
        :render-value="renderValue ?? $slots.value"
        :render-term="renderTerm ?? $slots.term"
        :input-id="inputId"
        :input-name="inputName"
    />
</template>

<style lang="scss">
.b4expr-input > div > .p-inputgroup > .p-dropdown .p-dropdown-label {
    color: #555;
}

.b4expr-input > div > .p-inputgroup > .p-dropdown {
    &:hover {
        opacity: 0.8;
    }
}
</style>
